import { fetchJSON as fetchAPI } from './client';

const BASE = import.meta.env.VITE_AUTOMATION_API ?? 'http://localhost:8163';

export type AutomationDashboard = {
  tenant_id: string;
  workspace_id?: string;
  active_workflows: number;
  draft_workflows: number;
  running_executions: number;
  failed_executions: number;
  completed_executions: number;
  pending_approvals: number;
  active_rules: number;
  registered_agents: number;
  active_agents: number;
  scheduled_jobs: number;
  templates_available: number;
  events_processed_24h: number;
  avg_execution_ms: number;
  recent_activity?: ActivityEntry[];
  generated_at: string;
};

export type ActivityEntry = {
  id: string;
  tenant_id: string;
  resource_id?: string;
  category: string;
  action: string;
  message: string;
  actor?: string;
  created_at: string;
};

export type WorkflowDefinition = {
  id: string;
  tenant_id: string;
  workspace_id: string;
  version: number;
  name: string;
  description?: string;
  status: string;
  template: boolean;
  trigger: { type: string; event_type?: string; schedule?: string };
  nodes: WorkflowNode[];
  created_at: string;
  updated_at: string;
};

export type WorkflowNode = {
  id: string;
  type: string;
  name: string;
  next?: string[];
  condition?: string;
  action?: string;
};

export type WorkflowExecution = {
  id: string;
  tenant_id: string;
  workspace_id: string;
  workflow_id: string;
  workflow_version: number;
  status: string;
  trigger_type?: string;
  current_node?: string;
  started_at: string;
  updated_at: string;
  completed_at?: string;
};

export type BusinessRule = {
  id: string;
  tenant_id: string;
  workspace_id: string;
  name: string;
  domain: string;
  expression: string;
  rule_type: string;
  priority: number;
  active: boolean;
  description?: string;
  created_at: string;
  updated_at: string;
};

export type AIAgent = {
  id: string;
  tenant_id: string;
  workspace_id: string;
  type: string;
  name: string;
  description?: string;
  capabilities?: string[];
  skills?: { id: string; name: string }[];
  requires_approval: boolean;
  sandbox: boolean;
  active: boolean;
  created_at: string;
};

export type WorkflowTemplate = {
  id: string;
  name: string;
  category: string;
  description?: string;
  tags?: string[];
  nodes: WorkflowNode[];
};

export type ApprovalRequest = {
  id: string;
  tenant_id: string;
  execution_id: string;
  node_id?: string;
  status: string;
  created_at: string;
  expires_at?: string;
};

export const automationApi = {
  dashboard: (workspaceId?: string) =>
    fetchAPI<AutomationDashboard>(`${BASE}/api/v1/automation/dashboard${workspaceId ? `?workspace_id=${workspaceId}` : ''}`),

  listWorkflows: (workspaceId?: string) =>
    fetchAPI<{ workflows: WorkflowDefinition[] }>(`${BASE}/api/v1/workflows${workspaceId ? `?workspace_id=${workspaceId}` : ''}`),

  createWorkflow: (body: { name: string; workspace_id?: string; description?: string; trigger?: object; nodes?: WorkflowNode[] }) =>
    fetchAPI<WorkflowDefinition>(`${BASE}/api/v1/workflows`, { method: 'POST', body: JSON.stringify(body) }),

  publishWorkflow: (id: string) =>
    fetchAPI<WorkflowDefinition>(`${BASE}/api/v1/workflows/${id}/publish`, { method: 'POST', body: '{}' }),

  executeWorkflow: (id: string, body?: { workspace_id?: string; variables?: Record<string, unknown> }) =>
    fetchAPI<WorkflowExecution>(`${BASE}/api/v1/workflows/${id}/execute`, { method: 'POST', body: JSON.stringify(body ?? {}) }),

  listExecutions: (workspaceId?: string) =>
    fetchAPI<{ executions: WorkflowExecution[] }>(`${BASE}/api/v1/workflows/executions${workspaceId ? `?workspace_id=${workspaceId}` : ''}`),

  listRules: (workspaceId?: string) =>
    fetchAPI<{ rules: BusinessRule[] }>(`${BASE}/api/v1/rules${workspaceId ? `?workspace_id=${workspaceId}` : ''}`),

  createRule: (body: { name: string; domain: string; expression: string; workspace_id?: string; active?: boolean }) =>
    fetchAPI<BusinessRule>(`${BASE}/api/v1/rules`, { method: 'POST', body: JSON.stringify(body) }),

  listAgents: (workspaceId?: string) =>
    fetchAPI<{ agents: AIAgent[] }>(`${BASE}/api/v1/agents${workspaceId ? `?workspace_id=${workspaceId}` : ''}`),

  createAgent: (body: { name: string; type: string; workspace_id?: string; capabilities?: string[]; skills?: string[] }) =>
    fetchAPI<AIAgent>(`${BASE}/api/v1/agents`, { method: 'POST', body: JSON.stringify(body) }),

  listTemplates: () =>
    fetchAPI<{ templates: WorkflowTemplate[] }>(`${BASE}/api/v1/automation/templates`),

  listApprovals: () =>
    fetchAPI<{ approvals: ApprovalRequest[] }>(`${BASE}/api/v1/automation/approvals`),
};
